import { arrayLast } from "../../lib";
import { Proposition as PropositionRaw, ValueProposition } from "./Proposition";

type ResultState<BlockResult> = "passing" | "notPassing" | BlockResult;

type EffectExecution<Effect, BlockResult, Context> = {
  effect: Effect;
  index: number;
  blockResult?: BlockResult;
  context: Context;
};

export type EffectStackExecutorProps<Effect, BlockResult, Context> = {
  isExecuting: boolean;
  stacks: EffectExecution<Effect, BlockResult, Context>[][];
};

export const effectStackExecutorFactory = <Proposition, BlockResult, Context>({
  stacks = [],
}: Partial<
  EffectStackExecutorProps<Proposition, BlockResult, Context>
> = {}): EffectStackExecutorProps<Proposition, BlockResult, Context> => {
  return { stacks, isExecuting: false };
};

export const EffectStackExecutor = <
  ValueProps extends {},
  AttachedProps extends {},
  BlockResult,
  Context
>(
  execution: EffectStackExecutorProps<
    PropositionRaw<ValueProps, AttachedProps>,
    BlockResult,
    Context
  >,
  executeValueProposition: (
    valueProp: ValueProposition<ValueProps, AttachedProps>,
    context: Context
  ) => ResultState<BlockResult>
) => {
  type Proposition = PropositionRaw<ValueProps, AttachedProps>;
  type ResultStateLocal = ResultState<BlockResult>;
  type EffectStack = EffectExecution<Proposition, BlockResult, Context>[];

  return {
    addInHead(effect: Proposition, context: Context) {
      execution.stacks = [
        [
          {
            effect,
            index: 0,
            context,
          },
        ],
        ...execution.stacks,
      ];
    },
    add(effect: Proposition, context: Context) {
      const { stacks } = execution;
      stacks.push([
        {
          effect,
          index: 0,
          context,
        },
      ]);
    },
    addAllInReverse(effects: Proposition[], context: Context) {
      [...effects].reverse().forEach((effect) => this.add(effect, context));
    },
    returnResult(result: ResultStateLocal, stack: EffectStack) {
      const lastExec = arrayLast(stack);
      const { stacks } = execution;
      if (!lastExec) {
        arrayDelete(stacks, stack);
        return;
      }
      const { effect, index } = lastExec;

      PropositionRaw.map(
        effect,
        (value) => {
          throw new Error(
            "unexpected: value propostion should not trigger children"
          );
        },
        ({ propositions, type }) => {
          if (
            (result === "notPassing" && type === "and") ||
            (result === "passing" && type === "or") ||
            index === propositions.length - 1
          ) {
            arrayDelete(stack, lastExec);
            this.returnResult(result, stack);
            return;
          }
          lastExec.index++;
        },
        () => {
          const isCause = index === 0;
          if (isCause) {
            if (result === "notPassing") {
              arrayDelete(stack, lastExec);
              this.returnResult("passing", stack);
            } else {
              lastExec.index = 1;
            }
          } else {
            arrayDelete(stack, lastExec);
            this.returnResult("passing", stack);
          }
        }
      );
    },
    executeNext() {
      const { stacks } = execution;
      const lastStack = arrayLast(stacks);
      if (!lastStack) return;
      const lastExec = arrayLast(lastStack);
      if (!lastExec) {
        throw new Error("unexpected: empty stack");
      }
      const { effect, index, blockResult, context } = lastExec;

      PropositionRaw.map(
        effect,
        (value) => {
          if (blockResult !== undefined) {
            return;
          }
          const result = executeValueProposition(value, context);
          switch (result) {
            case "passing":
            case "notPassing":
              ignoreError(() => arrayDelete(lastStack, lastExec));
              ignoreError(() => this.returnResult(result, lastStack));
              break;
            default:
              lastExec.blockResult = result;
              return;
          }
        },
        ({ propositions }) => {
          const currentProposition = propositions.at(index);
          if (!currentProposition) {
            throw new Error(`could not find proposition at index ${index}`);
          }
          lastStack.push({ effect: currentProposition, index: 0, context });
        },
        ({ cause, effect }) => {
          const currentProposition = index === 0 ? cause : effect;
          lastStack.push({ effect: currentProposition, index: 0, context });
        }
      );
    },
    executeAll() {
      if (execution.isExecuting) return;
      execution.isExecuting = true;
      let currentExec = this.getCurrentExec();
      while (currentExec && currentExec.blockResult === undefined) {
        this.executeNext();
        currentExec = this.getCurrentExec();
      }
      execution.isExecuting = false;
    },
    isEmpty() {
      const { stacks } = execution;
      return !stacks.length;
    },
    getCurrentExec() {
      const lastStack = this.getCurrentStack();
      if (!lastStack) return;
      return arrayLast(lastStack);
    },
    getCurrentStack() {
      const { stacks } = execution;
      return arrayLast(stacks);
    },
    answerBlockingResult(result: "passing" | "notPassing") {
      const { stacks } = execution;
      const lastStack = arrayLast(stacks);
      if (!lastStack) {
        throw new Error("unexpected: no stack");
      }
      const lastExec = arrayLast(lastStack);
      if (!lastExec) {
        throw new Error("unexpected: empty stack");
      }
      arrayDelete(lastStack, lastExec);
      this.returnResult(result, lastStack);
    },
  };
};

const arrayDelete = <T>(array: T[], item: T) => {
  const index = array.indexOf(item);
  if (index === -1) {
    throw new Error(
      `unexpected: could not find item ${JSON.stringify(
        item
      )} in array ${JSON.stringify(array)}`
    );
  } else {
    array.splice(index, 1);
  }
};

const ignoreError = (exec: () => void) => {
  try {
    exec();
  } catch (err) {
    console.warn(err);
  }
};
